/**
 * product-gallery-enhanced.js
 * Enhanced product gallery — Web Component for Shopify Stretch theme.
 *
 * Architecture:
 *   ProductGalleryEnhanced extends HTMLElement.
 *   One instance per product gallery on the page.
 *   Reads all config from data attributes and embedded JSON — no global state.
 *
 * Responsibilities:
 *   1. Carousel navigation (scroll-snap + programmatic scrolling via scrollTo).
 *   2. Thumbnail ↔ main carousel synchronisation (IntersectionObserver).
 *   3. Dot indicator sync.
 *   4. Prev/Next arrow state management.
 *   5. Autoplay with pause-on-hover / pause-on-focus.
 *   6. Keyboard navigation (ArrowLeft / ArrowRight / Home / End).
 *   7. Client-side variant media filtering (replicates Liquid alt-tag logic).
 *   8. Navigation to featured media on variant change.
 *
 * Variant media filtering:
 *   Product media items may have alt text like "Color Shirt#Color_Red".
 *   The "#OptionName_OptionValue" suffix identifies which variant group
 *   each image belongs to. When a variant is selected, images that belong
 *   to a DIFFERENT group value for any option are hidden.
 *   This replicates the server-side Liquid logic in product-gallery-enhanced.liquid.
 *
 * Event sources for variant changes:
 *   1. Custom "variant:change" event dispatched by Stretch theme's <variant-picker>
 *      on the product form (bubbles to document). Payload: { formId, variant }.
 *   2. Fallback: "change" event on the [name="id"] input inside the product form.
 *
 * Performance:
 *   - Uses IntersectionObserver (native, no polling).
 *   - Avoids re-rendering the DOM on variant change — only toggles [hidden].
 *   - Autoplay uses setInterval; cleared on disconnect and on user interaction.
 *   - scrollTo with {behavior: 'smooth'} is used sparingly (programmatic nav only).
 *   - The gallery re-initialises automatically after product-rerender updates the DOM.
 */

/* =============================================================================
   UTILITIES
   ============================================================================= */

/**
 * Safely parse JSON. Returns null on failure instead of throwing.
 * @param {string} str
 * @returns {*}
 */
function safeParse(str) {
  try { return JSON.parse(str); } catch { return null; }
}

/**
 * Scroll a container so that a target child element is fully visible.
 * Uses scrollTo so both horizontal (carousel) and vertical (thumbnails) work.
 *
 * @param {HTMLElement} container
 * @param {HTMLElement} target
 * @param {'smooth'|'instant'|'auto'} behavior
 */
function scrollContainerToItem(container, target, behavior = 'smooth') {
  if (!container || !target) return;

  // Horizontal containers (carousel, horizontal thumbnail strip)
  // Math.round prevents Safari subpixel rounding issues where a fractional
  // scrollTo target lands 0.5px off from the scroll-snap point, causing the
  // carousel to get "stuck between slides".
  const offsetLeft = Math.round(target.offsetLeft - container.offsetLeft);
  // Vertical containers (vertical thumbnail strip)
  const offsetTop = Math.round(target.offsetTop - container.offsetTop);

  container.scrollTo({ left: offsetLeft, top: offsetTop, behavior });
}

/* =============================================================================
   MAIN CLASS
   ============================================================================= */

class ProductGalleryEnhanced extends HTMLElement {

  /* ---------------------------------------------------------------------------
     LIFECYCLE
  --------------------------------------------------------------------------- */

  connectedCallback() {
    // Abort if no product media present (blank product in theme editor)
    if (!this.querySelector('.pge-slide')) return;

    this._init();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._removeVariantListeners();
    this._removeDragHandlers();
    // Clean up PhotoSwipe lightbox instance
    if (this._photoSwipeInstance) {
      this._photoSwipeInstance.destroy();
      this._photoSwipeInstance = null;
    }
    if (this._lightboxHandler) {
      this.removeEventListener('lightbox:open', this._lightboxHandler);
      this._lightboxHandler = null;
    }
    if (this._fixedZoomHandler) {
      const fixedZoomBtn = this.querySelector('.pge-zoom-btn-fixed');
      if (fixedZoomBtn) fixedZoomBtn.removeEventListener('click', this._fixedZoomHandler);
      this._fixedZoomHandler = null;
    }
  }

  /* ---------------------------------------------------------------------------
     INITIALISATION
  --------------------------------------------------------------------------- */

  /**
   * Full initialisation. Called on connectedCallback and after product-rerender
   * updates the DOM (the component is re-connected, triggering connectedCallback again).
   */
  _init() {
    // --- DOM references -------------------------------------------------------
    this.carousel      = this.querySelector('.pge-carousel');
    this.thumbStrip    = this.querySelector('.pge-thumbnails');
    this.prevBtn       = this.querySelector('.pge-arrow--prev');
    this.nextBtn       = this.querySelector('.pge-arrow--next');
    this.dotsContainer = this.querySelector('.pge-dots');

    if (!this.carousel) return;

    // --- Configuration from data attributes -----------------------------------
    this._autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
    this._autoplaySpeed   = (parseInt(this.getAttribute('data-autoplay-speed')) || 4) * 1000;
    this._loopEnabled     = this.getAttribute('data-loop') === 'true';
    this._formId          = this.getAttribute('form');
    this._sectionId       = this.getAttribute('data-section-id');
    this._productTitle    = this.getAttribute('data-product-title') || '';

    // --- Visible item collections (excludes [hidden] items) -------------------
    this._refreshCollections();

    // --- Find the initial active slide ----------------------------------------
    // is-initial is added by Liquid to the default/featured variant's media slide.
    const allSlides = [...this.querySelectorAll('.pge-slide')];
    const initialSlide = allSlides.find(s => s.classList.contains('is-initial') && !s.hidden);
    this._currentIndex = this._slides.indexOf(initialSlide);
    if (this._currentIndex < 0) this._currentIndex = 0;

    // Mark [data-single-slide] if only one visible slide (hides arrows and dots via CSS)
    if (this._slides.length <= 1) {
      this.setAttribute('data-single-slide', '');
    } else {
      this.removeAttribute('data-single-slide');
    }

    // --- Setup subsystems -----------------------------------------------------
    this._setupObserver();
    this._setupThumbnails();
    this._setupArrows();
    this._setupDots();
    this._setupKeyboard();
    this._setupAutoplayPause();
    this._setupVariantListeners();
    // Extension: drag-to-scroll (desktop pointer + Safari touch drag)
    this._setupDragScroll();
    // Extension: color swatch strip (only wires up if swatches exist in DOM)
    this._setupSwatches();
    // Apply the initial color filter based on the currently-selected variant.
    // This handles simple alt tags (e.g., alt="blue") that can't be pre-filtered server-side.
    this._applyInitialColorFilter();
    // Extension: lightbox/zoom via PhotoSwipe (Stretch theme default zoom system)
    this._setupLightbox();

    // Start autoplay if enabled and more than one slide
    if (this._autoplayEnabled && this._slides.length > 1) {
      this._startAutoplay();
    }

    // Scroll to the initial slide without animation (instant on load)
    requestAnimationFrame(() => {
      this._scrollToIndex(this._currentIndex, 'instant');
      this._syncUI(this._currentIndex);
    });
  }

  /**
   * Rebuild _slides, _thumbs, and _dots arrays from the live DOM.
   * Called after variant changes that show/hide elements.
   */
  _refreshCollections() {
    // Exclude both [hidden] (variant-filtered) and .hidden-variant-image (color-filtered)
    // so that navigation always operates on the currently visible set only.
    this._slides = [...this.querySelectorAll('.pge-slide:not([hidden]):not(.hidden-variant-image)')];
    this._thumbs  = [...this.querySelectorAll('.pge-thumb:not([hidden]):not(.hidden-variant-image)')];
    this._dots    = [...this.querySelectorAll('.pge-dot:not([hidden]):not(.hidden-variant-image)')];
  }

  /* ---------------------------------------------------------------------------
     INTERSECTION OBSERVER — tracks which slide is currently in view.
     Updates _currentIndex and calls _syncUI without scrolling (avoids loops).
  --------------------------------------------------------------------------- */

  _setupObserver() {
    if (this._observer) this._observer.disconnect();

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only act on slides that become at least 50% visible
          if (!entry.isIntersecting) return;

          const idx = this._slides.indexOf(entry.target);
          if (idx !== -1 && idx !== this._currentIndex) {
            this._currentIndex = idx;
            // Do NOT scroll here — we are responding to scroll, not causing it
            this._syncUI(idx, /* scroll= */ false);
          }
        });
      },
      {
        root: this.carousel,
        threshold: 0.5,  // slide must be at least 50% visible to register
      }
    );

    this._slides.forEach((slide) => this._observer.observe(slide));
  }

  /* ---------------------------------------------------------------------------
     UI SYNC — updates thumbnails, dots, and arrow disabled state
  --------------------------------------------------------------------------- */

  /**
   * Synchronise all UI controls to reflect the active slide index.
   *
   * @param {number} idx - index within this._slides (visible slides only)
   * @param {boolean} scrollThumb - whether to scroll the thumbnail into view
   */
  _syncUI(idx, scrollThumb = true) {
    // Thumbnails
    this._thumbs.forEach((thumb, i) => {
      const active = i === idx;
      thumb.setAttribute('aria-selected', String(active));
    });

    // Dots
    this._dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });

    // Scroll active thumbnail into view (both vertical and horizontal strips)
    if (scrollThumb && this.thumbStrip && this._thumbs[idx]) {
      scrollContainerToItem(this.thumbStrip, this._thumbs[idx], 'smooth');
    }

    // Arrow disabled state: disabled at boundaries unless loop mode is active.
    // In loop mode arrows are always enabled — there is no true "first" or "last".
    if (this.prevBtn) {
      const atStart = !this._loopEnabled && idx === 0;
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextBtn) {
      const atEnd = !this._loopEnabled && idx >= this._slides.length - 1;
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', String(atEnd));
    }
  }

  /* ---------------------------------------------------------------------------
     NAVIGATION
  --------------------------------------------------------------------------- */

  /**
   * Navigate to a slide by its visible-collection index.
   * Clamps to valid range; resets autoplay timer on manual navigation.
   *
   * @param {number} idx
   */
  _goTo(idx) {
    // When loop is enabled, wrap around using modular arithmetic so that
    // navigating past the last slide wraps to the first (and vice versa).
    // Without loop, clamp to [0, length-1] as before.
    const len = this._slides.length;
    const clamped = this._loopEnabled
      ? ((idx % len) + len) % len
      : Math.max(0, Math.min(idx, len - 1));

    if (clamped === this._currentIndex && idx >= 0 && idx < len) {
      // Same slide — still sync UI in case collections were refreshed
    }
    this._currentIndex = clamped;
    this._scrollToIndex(clamped, 'smooth');
    this._syncUI(clamped);

    // Reset autoplay timer after manual interaction to give the user a pause
    if (this._autoplayEnabled) {
      this._stopAutoplay();
      this._startAutoplay();
    }
  }

  /**
   * Programmatically scroll the carousel to a slide index.
   *
   * @param {number} idx
   * @param {'smooth'|'instant'} behavior
   */
  _scrollToIndex(idx, behavior = 'smooth') {
    const slide = this._slides[idx];
    if (!slide) return;
    scrollContainerToItem(this.carousel, slide, behavior);
  }

  /* ---------------------------------------------------------------------------
     THUMBNAILS
  --------------------------------------------------------------------------- */

  _setupThumbnails() {
    // Remove any existing listeners to avoid duplicates on re-init
    this._thumbHandlers = this._thumbHandlers || new Map();

    this._thumbs.forEach((thumb, i) => {
      // Clean up old handler if present
      if (this._thumbHandlers.has(thumb)) {
        thumb.removeEventListener('click', this._thumbHandlers.get(thumb));
      }
      const handler = () => this._goTo(i);
      this._thumbHandlers.set(thumb, handler);
      thumb.addEventListener('click', handler);
    });
  }

  /* ---------------------------------------------------------------------------
     ARROWS
  --------------------------------------------------------------------------- */

  _setupArrows() {
    // Use named bound methods so they can be removed on re-init
    if (!this._prevHandler) {
      this._prevHandler = () => this._goTo(this._currentIndex - 1);
    }
    if (!this._nextHandler) {
      this._nextHandler = () => this._goTo(this._currentIndex + 1);
    }

    this.prevBtn?.removeEventListener('click', this._prevHandler);
    this.nextBtn?.removeEventListener('click', this._nextHandler);
    this.prevBtn?.addEventListener('click', this._prevHandler);
    this.nextBtn?.addEventListener('click', this._nextHandler);
  }

  /* ---------------------------------------------------------------------------
     DOTS
  --------------------------------------------------------------------------- */

  _setupDots() {
    this._dotHandlers = this._dotHandlers || new Map();

    this._dots.forEach((dot, i) => {
      if (this._dotHandlers.has(dot)) {
        dot.removeEventListener('click', this._dotHandlers.get(dot));
      }
      const handler = () => this._goTo(i);
      this._dotHandlers.set(dot, handler);
      dot.addEventListener('click', handler);
    });
  }

  /* ---------------------------------------------------------------------------
     KEYBOARD NAVIGATION (A11y)
     Carousel is focusable (tabindex="0" set in Liquid).
     Arrow keys navigate slides; Home/End jump to first/last.
  --------------------------------------------------------------------------- */

  _setupKeyboard() {
    if (this._keyHandler) {
      this.carousel.removeEventListener('keydown', this._keyHandler);
    }
    this._keyHandler = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this._goTo(this._currentIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this._goTo(this._currentIndex + 1);
          break;
        case 'Home':
          e.preventDefault();
          this._goTo(0);
          break;
        case 'End':
          e.preventDefault();
          this._goTo(this._slides.length - 1);
          break;
      }
    };
    this.carousel.addEventListener('keydown', this._keyHandler);
  }

  /* ---------------------------------------------------------------------------
     AUTOPLAY
     Pauses on hover and on keyboard focus anywhere inside the component.
     Resumes when the pointer leaves or focus moves away.
  --------------------------------------------------------------------------- */

  _startAutoplay() {
    this._stopAutoplay(); // ensure no duplicate timers
    this._autoplayTimer = setInterval(() => {
      if (this._paused) return;
      const next = (this._currentIndex + 1) % this._slides.length;
      this._currentIndex = next;
      this._scrollToIndex(next, 'smooth');
      this._syncUI(next);
    }, this._autoplaySpeed);
  }

  _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
  }

  /**
   * Attach hover and focus listeners so autoplay pauses during user interaction.
   */
  _setupAutoplayPause() {
    if (!this._autoplayEnabled) return;

    this._pauseHandler  = () => { this._paused = true; };
    this._resumeHandler = () => { this._paused = false; };

    this.addEventListener('mouseenter', this._pauseHandler);
    this.addEventListener('mouseleave', this._resumeHandler);
    this.addEventListener('focusin',    this._pauseHandler);
    this.addEventListener('focusout',   this._resumeHandler);
  }

  /* ---------------------------------------------------------------------------
     VARIANT CHANGE HANDLING
     Two listeners are registered:
       1. "variant:change" — a custom event dispatched by the Stretch theme's
          <variant-picker> on the product form (bubbles to document).
          Payload: { formId: string, variant: object, previousVariant: object }.
       2. "change" on the product form — fallback for any selector that mutates
          the hidden [name="id"] input directly.
  --------------------------------------------------------------------------- */

  _setupVariantListeners() {
    this._removeVariantListeners(); // clean up before re-attaching

    // 1. Theme's custom variant:change event (dispatched by Stretch theme's <variant-picker>
    //    on the product form, bubbles up to document).
    //    Payload: { formId: <HTMLInputElement[name="id"]>, variant: object, previousVariant: object }
    //    NOTE: e.detail.formId is the form's [name="id"] input element (not a string).
    //    We extract the actual form ID by reading the input's parent form's id attribute.
    this._variantChangedHandler = (e) => {
      if (!e.detail?.variant) return;

      // Scope check: extract the form's ID from the event payload.
      // e.detail.formId is the [name="id"] input element — get its form's id attribute.
      const eventFormId = e.detail.formId;
      const formIdStr = (eventFormId && typeof eventFormId === 'object')
        ? (eventFormId.form ? eventFormId.form.id : '')
        : String(eventFormId || '');
      if (formIdStr && formIdStr !== this._formId) return;

      this._onVariantChanged(e.detail.variant);
    };
    document.addEventListener('variant:change', this._variantChangedHandler);

    // 2. Fallback: native change event on the product form
    const form = this._formId ? document.getElementById(this._formId) : null;
    if (form) {
      this._formChangeHandler = () => {
        const idInput = form.querySelector('[name="id"]');
        if (!idInput) return;
        const variantId = parseInt(idInput.value, 10);
        if (variantId) this._onVariantIdChanged(variantId);
      };
      form.addEventListener('change', this._formChangeHandler);
      this._boundForm = form;
    }
  }

  _removeVariantListeners() {
    if (this._variantChangedHandler) {
      document.removeEventListener('variant:change', this._variantChangedHandler);
      this._variantChangedHandler = null;
    }
    if (this._boundForm && this._formChangeHandler) {
      this._boundForm.removeEventListener('change', this._formChangeHandler);
      this._formChangeHandler = null;
      this._boundForm = null;
    }
  }

  /**
   * Called when the theme dispatches a variant:changed event with the full
   * variant object (preferred path).
   *
   * @param {{ id: number, options: string[], featured_media: { id: number } | null }} variant
   */
  _onVariantChanged(variant) {
    this._updateFilteredIndexes(variant);

    // When the color option changes, sync the gallery filter, swatch UI, and color label.
    // We compare against this._activeColor to avoid re-filtering when only size/style changes.
    const data = this._getEmbeddedData();
    if (data && data.colorOptionName && data.colorOptionIndex >= 0) {
      const newColor = (variant.options[data.colorOptionIndex] || '').toLowerCase();

      if (newColor && newColor !== this._activeColor) {
        this._activeColor = newColor;
        this._filterByColor(newColor);
        this._syncSwatchUI(newColor);
      }
    }

    if (variant.featured_media?.id) {
      this._navigateToMediaId(variant.featured_media.id);
    }
  }

  /**
   * Called when we only have the variant ID (fallback path).
   * Looks up the full variant object from the embedded JSON.
   *
   * @param {number} variantId
   */
  _onVariantIdChanged(variantId) {
    const data = this._getEmbeddedData();
    if (!data) return;
    const variant = data.variants.find((v) => v.id === variantId);
    if (variant) {
      this._onVariantChanged({
        id: variant.id,
        options: variant.options,
        featured_media: variant.featured_media_id ? { id: variant.featured_media_id } : null,
      });
    }
  }

  /* ---------------------------------------------------------------------------
     VARIANT MEDIA FILTERING
     Replicates the Liquid alt-tag filtering logic in client-side JavaScript.

     Logic (mirrors product-gallery-enhanced.liquid):
       For each media item that has "#OptionName_OptionValue" in its alt text:
         - Extract the option name and value from the alt text.
         - Find the index of that option name in product.options[].
         - Compare to the new variant's selected value for that option index.
         - If they don't match AND this media is not the variant's featured media → hide it.
  --------------------------------------------------------------------------- */

  /**
   * Update slide/thumbnail/dot visibility based on the selected variant.
   *
   * @param {{ id: number, options: string[], featured_media?: { id: number } | null }} variant
   */
  _updateFilteredIndexes(variant) {
    const data = this._getEmbeddedData();
    if (!data) return;

    const { mediaItems, options, productTitle } = data;
    const featuredMediaId = variant.featured_media?.id ?? null;

    // Compute the set of media positions that should be HIDDEN for this variant
    const filteredPositions = new Set();

    mediaItems.forEach((media) => {
      // Only apply filtering if the alt contains '#' and is not the product title
      if (!media.alt.includes('#') || media.alt === productTitle) return;

      const hashIdx = media.alt.lastIndexOf('#');
      const groupStr = media.alt.slice(hashIdx + 1);        // "Color_Red"
      const underIdx = groupStr.indexOf('_');
      if (underIdx === -1) return;

      const optionName  = groupStr.slice(0, underIdx).trim();   // "Color"
      const optionValue = groupStr.slice(underIdx + 1).trim().toLowerCase(); // "red"

      // Find option index in product.options (case-insensitive)
      const optionIdx = options.findIndex(
        (o) => o.toLowerCase() === optionName.toLowerCase()
      );
      if (optionIdx === -1) return;

      const selectedValue = (variant.options[optionIdx] || '').toLowerCase();

      // Hide if selected value doesn't match AND this isn't the featured media
      if (selectedValue !== optionValue && media.id !== featuredMediaId) {
        filteredPositions.add(media.position);
      }
    });

    // --- Apply visibility to slides ------------------------------------------
    const allSlides = [...this.querySelectorAll('.pge-slide')];
    allSlides.forEach((slide) => {
      const pos  = parseInt(slide.dataset.position, 10);
      const hide = filteredPositions.has(pos);
      slide.hidden = hide;
      slide.setAttribute('aria-hidden', String(hide));
    });

    // --- Apply visibility to thumbnails ---------------------------------------
    const allThumbs = [...this.querySelectorAll('.pge-thumb')];
    allThumbs.forEach((thumb) => {
      const pos  = parseInt(thumb.dataset.position, 10);
      const hide = filteredPositions.has(pos);
      thumb.hidden = hide;
    });

    // --- Apply visibility to dots --------------------------------------------
    const allDots = [...this.querySelectorAll('.pge-dot')];
    allDots.forEach((dot) => {
      const mediaId = parseInt(dot.dataset.mediaId, 10);
      const media   = mediaItems.find((m) => m.id === mediaId);
      if (media) dot.hidden = filteredPositions.has(media.position);
    });

    // --- Refresh collections and re-initialise subsystems --------------------
    this._refreshCollections();
    this._setupObserver();
    this._setupThumbnails();
    this._setupDots();

    // Single-slide attribute
    if (this._slides.length <= 1) {
      this.setAttribute('data-single-slide', '');
    } else {
      this.removeAttribute('data-single-slide');
    }

    // Recalculate zoom image-indexes so the lightbox opens at the correct position
    this._syncZoomIndexes();
  }

  /**
   * Scroll the carousel to the slide that contains the given media ID.
   * Only considers currently visible slides.
   *
   * @param {number} mediaId
   */
  _navigateToMediaId(mediaId) {
    const idx = this._slides.findIndex(
      (slide) => parseInt(slide.dataset.mediaId, 10) === mediaId
    );
    if (idx !== -1) {
      this._goTo(idx);
    }
  }

  /* ---------------------------------------------------------------------------
     DRAG-TO-SCROLL (Extension)
     Adds pointer-event-based drag scrolling on top of the existing scroll-snap
     carousel. This covers:
       • Desktop browsers (mouse drag on the main image area)
       • Safari on macOS (which historically had issues with scroll-snap + mouse drag)
       • iOS Safari (pointer events fall through to native touch scroll-snap)

     Implementation:
       - pointerdown  → capture pointer, record start position
       - pointermove  → update carousel.scrollLeft directly (smooth feel)
       - pointerup    → release capture, snap to nearest slide via _goTo
       - click capture → suppress click events that fire immediately after a drag

     Does NOT replace scroll-snap — the browser still enforces snap points after
     the drag ends (via the scrollend or after scrollTo).
  --------------------------------------------------------------------------- */

  /**
   * Attaches pointer event listeners for drag-to-scroll on the main carousel.
   * Called from _init(). Idempotent — removes previous handlers before attaching.
   *
   * Strategy:
   *   MOUSE pointers (desktop): JS captures the pointer on pointerdown and
   *   drives scrollLeft directly for a grab-and-drag feel. On pointerup, the
   *   nearest slide is determined and scrollTo snaps to it.
   *
   *   TOUCH/PEN pointers (mobile/tablet): Native CSS scroll-snap handles the
   *   entire gesture — momentum scrolling, rubber-banding, and snap alignment.
   *   The IntersectionObserver (already set up in _setupObserver) syncs the UI
   *   (thumbnails, dots, arrows) when the visible slide changes.
   *
   *   Why not use JS for touch?
   *   iOS Safari does not reliably honor setPointerCapture() on an active touch
   *   gesture. The browser's own gesture recogniser takes priority, and attempting
   *   to fight it causes laggy, inconsistent swipe behaviour and "stuck between
   *   slides" symptoms. Native scroll-snap with the correct CSS (touch-action:
   *   pan-y, scroll-snap-stop: always, overscroll-behavior-x: contain) provides
   *   the best iOS Safari experience.
   */
  _setupDragScroll() {
    if (!this.carousel) return;

    // Clean up any previous drag handlers before re-attaching
    this._removeDragHandlers();

    const carousel = this.carousel;

    // --- State (mouse drag only) ---
    let isDragging      = false;
    let startX          = 0;
    let startScrollLeft = 0;
    let hasDragged      = false;  // flag to suppress click-through after a drag

    /**
     * pointerdown: start tracking a mouse drag.
     * Touch/pen pointers are ignored — native scroll-snap handles those.
     */
    const onPointerDown = (e) => {
      // Only handle mouse (desktop). Touch/pen use native scroll-snap.
      if (e.pointerType === 'touch' || e.pointerType === 'pen') return;
      // Ignore right-click and middle-click
      if (e.button !== 0) return;
      // Don't intercept clicks on interactive children (zoom button, etc.)
      if (e.target.closest('button, a, open-lightbox-button')) return;

      isDragging      = true;
      hasDragged      = false;
      startX          = e.clientX;
      startScrollLeft = carousel.scrollLeft;

      // Pointer capture routes all subsequent pointer events here even if the
      // pointer leaves the carousel — essential for fast drags.
      carousel.setPointerCapture(e.pointerId);
      carousel.classList.add('is-dragging');
    };

    /**
     * pointermove: translate horizontal pointer delta to a scroll offset.
     * Only meaningful during an active mouse drag.
     */
    const onPointerMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      // Mark as a drag once the pointer moves more than 5px (avoids false positives)
      if (Math.abs(dx) > 5) hasDragged = true;
      // Direct scrollLeft assignment gives immediate, lag-free response
      carousel.scrollLeft = startScrollLeft - dx;
    };

    /**
     * pointerup / pointercancel: finish the drag and snap to the nearest slide.
     */
    const onPointerUp = (e) => {
      if (!isDragging) return;
      isDragging = false;

      carousel.releasePointerCapture(e.pointerId);
      carousel.classList.remove('is-dragging');

      if (!hasDragged) return;

      // Determine the nearest slide based on the current scrollLeft position
      const firstSlide = this._slides[0];
      const slideWidth = firstSlide ? firstSlide.offsetWidth : 0;
      if (slideWidth > 0) {
        const raw     = carousel.scrollLeft / slideWidth;
        const nearest = Math.round(raw);
        const clamped = Math.max(0, Math.min(nearest, this._slides.length - 1));
        if (clamped !== this._currentIndex) {
          this._goTo(clamped);
        } else {
          // Same slide — still snap-correct position in case drag overshot slightly
          this._scrollToIndex(clamped, 'smooth');
        }
      }
    };

    /**
     * click (capture phase): suppress click events that fire immediately after
     * a completed drag so that clicking an image to zoom doesn't fire accidentally.
     */
    const onClick = (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    };

    carousel.addEventListener('pointerdown',   onPointerDown);
    carousel.addEventListener('pointermove',   onPointerMove);
    carousel.addEventListener('pointerup',     onPointerUp);
    carousel.addEventListener('pointercancel', onPointerUp);
    // capture: true ensures this fires before child click handlers
    carousel.addEventListener('click', onClick, { capture: true });

    // Store refs for cleanup in disconnectedCallback / re-init
    this._dragHandlers = { onPointerDown, onPointerMove, onPointerUp, onClick };
  }

  /**
   * Removes all drag-related event listeners.
   * Called from disconnectedCallback and at the start of _setupDragScroll().
   */
  _removeDragHandlers() {
    if (!this._dragHandlers || !this.carousel) return;
    const { onPointerDown, onPointerMove, onPointerUp, onClick } = this._dragHandlers;
    const carousel = this.carousel;
    carousel.removeEventListener('pointerdown',   onPointerDown);
    carousel.removeEventListener('pointermove',   onPointerMove);
    carousel.removeEventListener('pointerup',     onPointerUp);
    carousel.removeEventListener('pointercancel', onPointerUp);
    carousel.removeEventListener('click', onClick, { capture: true });
    this._dragHandlers = null;
  }

  /* ---------------------------------------------------------------------------
     COLOR VARIANT SWATCHES (Extension)
     Handles the swatch strip rendered by the Liquid snippet below the gallery.

     Safety rule: visibility is toggled ONLY via the CSS class .hidden-variant-image.
     The [hidden] attribute (used by the existing variant filtering system) is
     NEVER touched here — the two systems are fully independent and composable.

     Flow:
       1. User clicks a color swatch.
       2. _onSwatchClick() updates aria-pressed on all swatches.
       3. _filterByColor() adds/removes .hidden-variant-image on slides,
          thumbnails, and dots.
       4. _refreshCollections() rebuilds _slides/_thumbs/_dots.
       5. If the currently-active slide became hidden, navigate to index 0.
       6. _syncColorToForm() attempts to select the matching color option
          in the product form so the variant selector stays in sync.
  --------------------------------------------------------------------------- */

  /**
   * Wire click handlers onto .pge-swatch buttons.
   * Safe to call multiple times — cleans up old handlers first.
   */
  _setupSwatches() {
    const swatches = [...this.querySelectorAll('.pge-swatch')];
    if (!swatches.length) return;

    this._swatchHandlers = this._swatchHandlers || new Map();

    swatches.forEach((swatch) => {
      // Remove stale handler if present (e.g., after product-rerender)
      if (this._swatchHandlers.has(swatch)) {
        swatch.removeEventListener('click', this._swatchHandlers.get(swatch));
      }
      const handler = () => {
        const colorValue = swatch.getAttribute('data-color-value');
        if (colorValue) this._onSwatchClick(swatch, colorValue);
      };
      this._swatchHandlers.set(swatch, handler);
      swatch.addEventListener('click', handler);
    });
  }

  /**
   * Handle a swatch button click.
   * Each swatch represents a specific color — clicking it triggers the actual
   * Shopify variant change via _syncColorToForm(), which updates the product form
   * and fires the variant:changed event. The gallery filter + UI updates then
   * happen automatically via _onVariantChanged().
   *
   * @param {HTMLElement} clickedSwatch
   * @param {string} colorValue - lowercased color value
   */
  _onSwatchClick(_clickedSwatch, colorValue) {
    // Update aria-pressed on ALL swatches (both desktop_view and mobile_view groups)
    // Match by data-color-value so both groups stay synchronized
    const allSwatches = [...this.querySelectorAll('.pge-swatch')];
    allSwatches.forEach((s) => {
      const sv = s.getAttribute('data-color-value');
      s.setAttribute('aria-pressed', sv === colorValue ? 'true' : 'false');
    });

    // Track the active color so _onVariantChanged can skip re-filtering when
    // the user switches size/style without changing color.
    this._activeColor = colorValue;

    // Filter gallery slides by the selected color (CSS class only, no DOM changes)
    this._filterByColor(colorValue);
 this._syncSwatchUI(colorValue);
    // Trigger the actual Shopify variant change via the product form's color selector.
    // This fires the theme's variant:changed event, updating price, availability, etc.
    this._syncColorToForm(colorValue);
  }

  /**
   * Show/hide slides, thumbnails, and dots based on color value by toggling
   * the .hidden-variant-image CSS class.
   *
   * IMPORTANT: this method only touches .hidden-variant-image.
   * It does NOT touch the [hidden] attribute managed by _updateFilteredIndexes().
   * Both systems can be active simultaneously.
   *
   * @param {string} colorValue - lowercased color value, or "__all__" to clear filter
   */
  _filterByColor(colorValue) {
    const data = this._getEmbeddedData();
    if (!data) return;

    const { mediaItems, options, colorOptionName } = data;

    // Build the set of media positions that should be color-hidden
    const shouldHide = new Set();

    if (colorValue !== '__all__' && colorOptionName) {
      // Find the index of the color option in the options array
      const colorOptIdx = options.findIndex(
        (o) => o.toLowerCase() === colorOptionName.toLowerCase()
      );

      if (colorOptIdx !== -1) {
        // All known color values (lowercased) — used to identify "color-tagged" images
        // in the simple alt format so that generic/untagged images are never hidden.
        const colorOptionValues = (data.colorOptionValues || []).map((v) => v.toLowerCase());
        const targetColor = colorValue.toLowerCase();

        mediaItems.forEach((media) => {
          const altLower = (media.alt || '').toLowerCase().trim();

          if (media.alt && media.alt.includes('#') && media.alt !== this._productTitle) {
            // ---- Maestrooo alt-tag format: "Image name#OptionName_OptionValue" ----
            // Example: "Blue shirt product image#Color_Blue"
            const hashIdx  = media.alt.lastIndexOf('#');
            const groupStr = media.alt.slice(hashIdx + 1);   // e.g. "Color_Blue"
            const underIdx = groupStr.indexOf('_');
            if (underIdx === -1) return;

            const optName  = groupStr.slice(0, underIdx).trim();
            const optValue = groupStr.slice(underIdx + 1).trim().toLowerCase();

            // Only act on tags that match the color option name
            const tagOptIdx = options.findIndex(
              (o) => o.toLowerCase() === optName.toLowerCase()
            );
            if (tagOptIdx !== colorOptIdx) return;

            // Hide if this media's color value doesn't match the selected color
            if (optValue !== targetColor) {
              shouldHide.add(media.position);
            }
          } else {
            // ---- Simple alt-tag format: alt text equals or contains the color name ----
            // Example: alt="blue", alt="blue shirt front", alt="Green"
            //
            // Only filter images whose alt is a known color option value.
            // Generic images (product title, empty alt, non-color descriptions)
            // are treated as "global" and shown for every color variant.
            if (colorOptionValues.length === 0) return; // no color values to check against

            const isColorTagged = colorOptionValues.some(
              (cv) => altLower === cv || altLower.includes(cv)
            );

            // If this image is tagged as a color but NOT the active color → hide it
            if (isColorTagged && !altLower.includes(targetColor)) {
              shouldHide.add(media.position);
            }
          }
        });
      }
    }

    // --- Slides ---------------------------------------------------------------
    const allSlides = [...this.querySelectorAll('.pge-slide')];
    allSlides.forEach((slide) => {
      const pos  = parseInt(slide.dataset.position, 10);
      const hide = shouldHide.has(pos);
      slide.classList.toggle('hidden-variant-image', hide);
      // Keep aria-hidden in sync so the zoom lightbox can skip hidden slides.
      // Only remove aria-hidden when the [hidden] variant filter also says visible.
      if (hide) {
        slide.setAttribute('aria-hidden', 'true');
      } else if (!slide.hidden) {
        slide.removeAttribute('aria-hidden');
      }
    });

    // --- Thumbnails -----------------------------------------------------------
    const allThumbs = [...this.querySelectorAll('.pge-thumb')];
    allThumbs.forEach((thumb) => {
      const pos = parseInt(thumb.dataset.position, 10);
      thumb.classList.toggle('hidden-variant-image', shouldHide.has(pos));
    });

    // --- Dots -----------------------------------------------------------------
    const allDots = [...this.querySelectorAll('.pge-dot')];
    allDots.forEach((dot) => {
      const mediaId = parseInt(dot.dataset.mediaId, 10);
      const media   = mediaItems.find((m) => m.id === mediaId);
      if (media) dot.classList.toggle('hidden-variant-image', shouldHide.has(media.position));
    });

    // Rebuild navigation collections so arrows/dots/thumbs reflect new visibility
    this._refreshCollections();
    this._setupObserver();
    this._setupThumbnails();
    this._setupDots();

    // Single-slide attribute update
    if (this._slides.length <= 1) {
      this.setAttribute('data-single-slide', '');
    } else {
      this.removeAttribute('data-single-slide');
    }

    // If the active slide is now hidden, jump to the first visible slide
    const activeSlideStillVisible = this._slides.some(
      (s) => parseInt(s.dataset.position, 10) === parseInt(
        this.querySelectorAll('.pge-slide')[this._currentIndex]?.dataset.position, 10
      )
    );
    if (!activeSlideStillVisible && this._slides.length > 0) {
      this._goTo(0);
    }

    // Recalculate zoom image-indexes so the lightbox opens at the correct position
    this._syncZoomIndexes();
  }

  /**
   * After a swatch click, try to update the product form's color selector so
   * the variant selection stays consistent with the gallery filter.
   * Handles both <select> and radio-button style variant selectors.
   * Fails silently — the gallery still works even if the form isn't found.
   *
   * @param {string} colorValue - lowercased color value to select in the form
   */
  _syncColorToForm(colorValue) {
    const data = this._getEmbeddedData();
    if (!data || !data.colorOptionName) return;

    // Color option position (1-based, matching Shopify's option.position)
    const colorOptPos = data.colorOptionIndex + 1;

    // ---- Stretch theme variant-picker (primary path) ----
    // The Stretch theme renders radio inputs with:
    //   name="${formId}-option${position}"
    //   data-option-position="${position}"
    //   value="${option_value.id}" (numeric Shopify ID, NOT the color name)
    // These radios live inside <variant-picker> → fieldset.option_color (hidden by our CSS).
    // We identify the correct radio by matching its associated <label> text to our colorValue.
    const sectionEl = this.closest('[id^="shopify-section-"]') || document;
    const radios = [...sectionEl.querySelectorAll(
      `input[type="radio"][data-option-position="${colorOptPos}"]`
    )];

    for (const radio of radios) {
      // Each radio has a matching <label for="radioId"> containing a .sr-only span with
      // the color name text (e.g., "Blue"). Match this text against our colorValue.
      const label = sectionEl.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      if (!label) continue;

      const labelText = (label.textContent || '').trim().toLowerCase();
      if (labelText === colorValue.toLowerCase()) {
        if (!radio.checked) {
          radio.checked = true;
          // Dispatch change event — the <variant-picker> custom element listens for this
          // and fires variant:changed, updating price, availability, URL, etc.
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }

    // ---- Fallback: standard Shopify form inputs (non-Stretch themes) ----
    const form = this._formId ? document.getElementById(this._formId) : null;
    if (!form) return;
    const optionNum = colorOptPos;

    // Radio with name="option${N}" and text value
    const fallbackRadios = [...form.querySelectorAll(`[name="option${optionNum}"]`)];
    const target = fallbackRadios.find(
      (r) => r.value.toLowerCase() === colorValue.toLowerCase()
    );
    if (target && !target.checked) {
      target.click();
      return;
    }

    // Select dropdown with name="option${N}"
    const select = form.querySelector(`select[name="option${optionNum}"]`);
    if (select) {
      const opt = [...select.options].find(
        (o) => o.value.toLowerCase() === colorValue.toLowerCase()
      );
      if (opt && select.value !== opt.value) {
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* ---------------------------------------------------------------------------
     INITIAL COLOR FILTER
     Apply the correct color filter on page load based on the variant that is
     already selected in the product form. This is necessary for products whose
     images use simple alt tags (e.g., alt="blue") rather than the Maestrooo
     "#OptionName_OptionValue" convention, since simple alt tags cannot be
     pre-filtered server-side.
  --------------------------------------------------------------------------- */

  /**
   * Reads the currently-selected color from the product form and applies the
   * matching color filter to the gallery. Called once from _init().
   *
   * Priority chain:
   *   1. data-initial-color attribute (server-computed from selected_or_first_available_variant — most reliable)
   *   2. URL ?variant= parameter (handles direct links / shared URLs)
   *   3. Stretch theme radio buttons (may not be checked during early init)
   *   4. Standard Shopify form inputs (non-Stretch themes)
   *   5. First variant from embedded JSON (last resort)
   */
  _applyInitialColorFilter() {
    const data = this._getEmbeddedData();
    if (!data || !data.colorOptionName || data.colorOptionIndex < 0) return;

    const optionNum = data.colorOptionIndex + 1; // Shopify uses 1-based option numbering
    let colorValue  = null;

    // 1. Server-computed initial color (matches product.selected_or_first_available_variant).
    //    This is the most reliable source — immune to browser form restoration and JS race conditions.
    const initialColor = this.getAttribute('data-initial-color');
    if (initialColor) {
      colorValue = initialColor.toLowerCase();
    }

    // 2. URL variant parameter — handles direct/shared links where the URL variant
    //    may differ from the default first-available variant.
    if (!colorValue) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = parseInt(urlParams.get('variant'), 10);
        if (urlVariantId) {
          const urlVariant = data.variants.find((v) => v.id === urlVariantId);
          if (urlVariant) {
            colorValue = (urlVariant.options[data.colorOptionIndex] || '').toLowerCase();
          }
        }
      } catch (_) { /* URLSearchParams not supported — continue to next fallback */ }
    }

    // 3. Stretch theme radio buttons (data-option-position on radios).
    //    The radio value is a numeric Shopify ID, so we read the color name from the label.
    if (!colorValue) {
      const sectionEl = this.closest('[id^="shopify-section-"]') || document;
      const checkedRadio = sectionEl.querySelector(
        `input[type="radio"][data-option-position="${optionNum}"]:checked`
      );
      if (checkedRadio) {
        const label = sectionEl.querySelector(`label[for="${CSS.escape(checkedRadio.id)}"]`);
        if (label) {
          colorValue = (label.textContent || '').trim().toLowerCase();
        }
      }
    }

    // 4. Standard Shopify form inputs (non-Stretch themes).
    if (!colorValue) {
      const form = this._formId ? document.getElementById(this._formId) : null;
      if (form) {
        const stdRadio = form.querySelector(`[name="option${optionNum}"]:checked`);
        if (stdRadio) {
          colorValue = stdRadio.value.toLowerCase();
        } else {
          const select = form.querySelector(`select[name="option${optionNum}"]`);
          if (select && select.value) colorValue = select.value.toLowerCase();
        }
      }
    }

    // 5. First variant from embedded JSON (last resort).
    if (!colorValue && data.variants.length > 0) {
      colorValue = (data.variants[0].options[data.colorOptionIndex] || '').toLowerCase();
    }

    if (colorValue) {
      this._activeColor = colorValue;
      this._filterByColor(colorValue);
      this._syncSwatchUI(colorValue);
    }
  }

  /* ---------------------------------------------------------------------------
     SWATCH UI SYNC
     Updates aria-pressed on all gallery swatch buttons to reflect the
     currently-active color. Separated from _onSwatchClick so that
     _onVariantChanged and _applyInitialColorFilter can also call it.
  --------------------------------------------------------------------------- */

  /**
   * Set aria-pressed="true" on the swatch matching colorValue, false on all others.
   * @param {string|null} colorValue - lowercased color value, or null/'__all__' for "All"
   */
  _syncSwatchUI(colorValue) {
    // Update swatch aria-pressed states
    const swatches = [...this.querySelectorAll('.pge-swatch')];
    swatches.forEach((s) => {
      const sv       = s.getAttribute('data-color-value');
      const isActive = colorValue
        ? sv === colorValue
        : sv === '__all__';
      s.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Update ALL "COLOR: BLUE" label texts (both desktop_view and mobile_view).
    // The label elements are rendered by the Liquid snippet with data-pge-color-value.
    const labelEls = [...this.querySelectorAll('.pge-color-label__value')];
    labelEls.forEach((labelEl) => {
      if (colorValue && colorValue !== '__all__') {
        labelEl.textContent = colorValue.toUpperCase();
      } else if (colorValue === '__all__') {
        labelEl.textContent = 'ALL';
      }
    });

    // Update the Stretch theme's native variant picker color label.
    // The variant picker renders: <fieldset class="option_color">
    //   <div class="variant-picker__option-info"><div class="h-stack"><legend>Color:</legend><span>Blue</span></div>
    // We find the <span> next to the Color legend and update its text.
    const sectionEl = this.closest('[id^="shopify-section-"]') || document;
    const colorFieldset = sectionEl.querySelector('fieldset.option_color');
    if (colorFieldset && colorValue && colorValue !== '__all__') {
      const valueSpan = colorFieldset.querySelector('.variant-picker__option-info .h-stack > span');
      if (valueSpan) {
        // Capitalize first letter to match the theme's display format (e.g., "Blue" not "blue")
        valueSpan.textContent = colorValue.charAt(0).toUpperCase() + colorValue.slice(1);
      }
    }

  }

  /* ---------------------------------------------------------------------------
     ZOOM INDEX SYNC
     The theme's open-lightbox-button uses an image-index attribute to open the
     lightbox at the correct position. When color filtering hides some slides,
     the indexes need to be recalculated so the zoom opens on the right image.
  --------------------------------------------------------------------------- */

  /**
   * No-op retained for backward compatibility — called from _filterByColor and
   * _updateFilteredIndexes. The single fixed zoom button computes its index at
   * click time via _getCurrentImageIndex(), so pre-syncing is unnecessary.
   */
  _syncZoomIndexes() {}

  /**
   * Compute the lightbox index for the currently visible carousel slide.
   * Maps _currentIndex (index within ALL visible slides) to an index within
   * visible IMAGE-type slides only (since the lightbox only shows images).
   *
   * @returns {number} 0-based index among visible image slides
   */
  _getCurrentImageIndex() {
    const currentSlide = this._slides[this._currentIndex];
    if (!currentSlide || currentSlide.dataset.mediaType !== 'image') return 0;

    let imageIdx = 0;
    for (const slide of this._slides) {
      if (slide === currentSlide) return imageIdx;
      if (slide.dataset.mediaType === 'image') imageIdx++;
    }
    return 0;
  }

  /* ---------------------------------------------------------------------------
     LIGHTBOX / ZOOM (Stretch Theme PhotoSwipe Integration)
     The Stretch theme's zoom uses PhotoSwipe, triggered by the `lightbox:open`
     custom event dispatched by `<open-lightbox-button>`. In the original theme,
     the `<product-gallery>` element listens for this event. Since we use
     `<product-gallery-enhanced>`, we listen here and open PhotoSwipe ourselves.

     This preserves the exact same zoom behavior, UI, and theme styling as the
     original Stretch theme gallery — no custom zoom implementation.
  --------------------------------------------------------------------------- */

  /**
   * Attach the lightbox:open event listener if zoom is enabled.
   * Called from _init(). Idempotent — cleans up before re-attaching.
   */
  _setupLightbox() {
    if (!this.hasAttribute('allow-zoom')) return;

    // Remove any previous listener to prevent duplicates on re-init
    if (this._lightboxHandler) {
      this.removeEventListener('lightbox:open', this._lightboxHandler);
    }
    this._lightboxHandler = (event) => {
      const index = event?.detail?.index;
      this._openLightbox(index);
    };
    this.addEventListener('lightbox:open', this._lightboxHandler);

    // Wire up the single fixed zoom button (positioned on .pge-main)
    const fixedZoomBtn = this.querySelector('.pge-zoom-btn-fixed');
    if (fixedZoomBtn) {
      if (this._fixedZoomHandler) {
        fixedZoomBtn.removeEventListener('click', this._fixedZoomHandler);
      }
      this._fixedZoomHandler = () => {
        this._openLightbox(this._getCurrentImageIndex());
      };
      fixedZoomBtn.addEventListener('click', this._fixedZoomHandler);
    }
  }

  /**
   * Extract the highest-resolution URL from an <img> element's srcset.
   * Falls back to img.src if srcset is absent or unparseable.
   *
   * @param {HTMLImageElement} img
   * @returns {string} highest resolution image URL
   */
  _getBestImageSrc(img) {
    const srcset = img.getAttribute('srcset') || '';
    if (!srcset) return img.src;

    let bestUrl = img.src;
    let bestWidth = 0;

    srcset.split(',').forEach((entry) => {
      const parts = entry.trim().split(/\s+/);
      if (parts.length >= 2) {
        const w = parseInt(parts[1], 10);
        if (w > bestWidth) {
          bestWidth = w;
          bestUrl = parts[0];
        }
      }
    });

    return bestUrl || img.src;
  }

  /**
   * Open the PhotoSwipe lightbox for the visible image slides.
   * Creates a fresh PhotoSwipe Lightbox instance each time to ensure the
   * data source, arrow handlers, and slide count are always correct —
   * even after color filtering changes which images are visible.
   *
   * Dynamically imports PhotoSwipeLightbox from the theme's vendor bundle.
   * The module reference is cached after first import for performance.
   *
   * @param {number|null} startIndex - image index to open at
   */
  async _openLightbox(startIndex) {
    // Build the data source from currently visible image slides only.
    // This ensures the lightbox respects color filtering — hidden images are excluded.
    const visibleSlides = this._slides.filter(
      (s) => s.dataset.mediaType === 'image'
    );

    const dataSource = visibleSlides.map((slide) => {
      const img = slide.querySelector('img');
      if (!img) return null;

      return {
        thumbnailElement: img,
        src: this._getBestImageSrc(img),
        msrc: img.currentSrc || img.src,
        width: parseInt(img.getAttribute('width'), 10) || img.naturalWidth || 1200,
        height: parseInt(img.getAttribute('height'), 10) || img.naturalHeight || 1200,
        alt: img.alt || '',
      };
    }).filter(Boolean);

    if (dataSource.length === 0) return;

    // Destroy any previous PhotoSwipe instance to ensure clean state.
    // This prevents stale slide data, broken navigation, and event handler leaks.
    if (this._photoSwipeInstance) {
      try { this._photoSwipeInstance.destroy(); } catch (_) {}
      this._photoSwipeInstance = null;
    }

    // Cache the PhotoSwipe module reference after first import (avoids re-fetching).
    if (!this._pswpModuleCache) {
      try {
        const vendorModule = await import('vendor');
        this._pswpModuleCache = vendorModule.PhotoSwipeLightbox;
        this._pswpCoreModule = () => import('photoswipe');
      } catch (err) {
        console.warn('[product-gallery-enhanced] Could not load PhotoSwipe for zoom:', err);
        return;
      }
    }

    const PhotoSwipeLightbox = this._pswpModuleCache;

    const lightbox = new PhotoSwipeLightbox({
      pswpModule: this._pswpCoreModule,
      dataSource: dataSource,
      bgOpacity: 1,
      mainClass: `color-scheme--${this.getAttribute('zoom-color-scheme-id') || ''}`,
      maxZoomLevel: parseInt(this.getAttribute('allow-zoom'), 10) || 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image failed to load',
      arrowPrevSVG: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>',
      arrowNextSVG: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>',
      counter: false,
      close: false,
      zoom: false,
    });

    // Register the close button UI matching the Stretch theme's lightbox
    lightbox.on('uiRegister', () => {
      lightbox.pswp.ui.registerElement({
        name: 'close-button circle-button circle-button--xl',
        className: '',
        ariaLabel: window.themeVariables?.strings?.closeGallery || 'Close gallery',
        order: 2,
        isButton: true,
        html: '<svg aria-hidden="true" focusable="false" fill="none" width="14" class="icon" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/></svg>',
        onClick: () => lightbox.pswp.close(),
      });
    });

    lightbox.addFilter('thumbEl', (_el, data) => data.thumbnailElement);

    // Arrow enable/disable: disable prev on first image, next on last image.
    // Registered on the lightbox which proxies 'change' events from the core pswp instance.
    lightbox.on('change', () => {
      const pswp = lightbox.pswp;
      if (!pswp) return;
      const total = pswp.getNumItems();
      const idx = pswp.currIndex;
      const prevBtn = pswp.element?.querySelector('.pswp__button--arrow--prev');
      const nextBtn = pswp.element?.querySelector('.pswp__button--arrow--next');
      if (prevBtn) {
        prevBtn.disabled = idx === 0;
        prevBtn.style.opacity = idx === 0 ? '0.3' : '1';
        prevBtn.style.pointerEvents = idx === 0 ? 'none' : 'auto';
      }
      if (nextBtn) {
        nextBtn.disabled = idx >= total - 1;
        nextBtn.style.opacity = idx >= total - 1 ? '0.3' : '1';
        nextBtn.style.pointerEvents = idx >= total - 1 ? 'none' : 'auto';
      }
    });

    // Apply arrow state after the opening animation completes
    lightbox.on('openingAnimationEnd', () => {
      const pswp = lightbox.pswp;
      if (!pswp) return;
      // Manually trigger the change handler to set initial arrow states
      const total = pswp.getNumItems();
      const idx = pswp.currIndex;
      const prevBtn = pswp.element?.querySelector('.pswp__button--arrow--prev');
      const nextBtn = pswp.element?.querySelector('.pswp__button--arrow--next');
      if (prevBtn) {
        prevBtn.disabled = idx === 0;
        prevBtn.style.opacity = idx === 0 ? '0.3' : '1';
        prevBtn.style.pointerEvents = idx === 0 ? 'none' : 'auto';
      }
      if (nextBtn) {
        nextBtn.disabled = idx >= total - 1;
        nextBtn.style.opacity = idx >= total - 1 ? '0.3' : '1';
        nextBtn.style.pointerEvents = idx >= total - 1 ? 'none' : 'auto';
      }
    });

    // Store reference for cleanup in disconnectedCallback
    this._photoSwipeInstance = lightbox;

    lightbox.init();
    lightbox.loadAndOpen(startIndex ?? 0, dataSource);
  }

  /* ---------------------------------------------------------------------------
     EMBEDDED JSON DATA
  --------------------------------------------------------------------------- */

  /**
   * Parse and return the embedded media/variant JSON.
   * Cached after first parse.
   *
   * @returns {{ mediaItems: object[], variants: object[], options: string[], productTitle: string } | null}
   */
  _getEmbeddedData() {
    if (this._embeddedData) return this._embeddedData;
    const el = this.querySelector('[data-pge-media]');
    if (!el) return null;
    this._embeddedData = safeParse(el.textContent);
    return this._embeddedData;
  }
}

/* =============================================================================
   REGISTRATION
   Guard prevents errors if the snippet is rendered multiple times on the same
   page (e.g., featured product section + main product section).
   ============================================================================= */
if (!customElements.get('product-gallery-enhanced')) {
  customElements.define('product-gallery-enhanced', ProductGalleryEnhanced);
}
